import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_URL = "https://www.google.com/search?q=JyVis+Assurances";

const Stars = ({ value, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5" aria-label={`${value} sur 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-[#FBBC04] text-[#FBBC04]" : "text-slate-300"}`} />
    ))}
  </div>
);

const ReviewCard = ({ r, i }) => (
  <article className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid={`google-review-${i}`}>
    <div className="flex items-center gap-3">
      {r.author_photo
        ? <img src={r.author_photo} alt="" referrerPolicy="no-referrer" className="w-10 h-10 rounded-full" />
        : <div className="w-10 h-10 rounded-full bg-[#0A1864] text-white flex items-center justify-center font-semibold">{r.author.charAt(0)}</div>}
      <div>
        <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 hover:underline">{r.author}</a>
        <p className="text-xs text-slate-500">{r.when}</p>
      </div>
    </div>
    <div className="mt-3"><Stars value={r.rating} /></div>
    <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{r.text}</p>
    <p className="mt-auto pt-4 text-xs text-slate-400">Avis publié sur Google</p>
  </article>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/google/reviews`).then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, []);

  const live = data?.available;
  const viewUrl = (live && data.maps_url) || FALLBACK_URL;
  const writeUrl = (live && data.write_review_url) || FALLBACK_URL;

  return (
    <div data-testid="google-reviews-widget">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <span className="text-2xl font-bold"><span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC04]">o</span><span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span></span>
        {live && data.rating && (
          <div className="flex items-center gap-3" data-testid="google-reviews-summary">
            <span className="text-4xl font-bold text-[#0A1864]">{String(data.rating).replace(".", ",")}</span>
            <div><Stars value={data.rating} size="w-5 h-5" /><p className="text-sm text-slate-500 mt-1">{data.count} avis</p></div>
          </div>
        )}
        {!live && data && (
          <div className="flex items-center gap-3" data-testid="google-reviews-summary">
            <span className="text-4xl font-bold text-[#0A1864]">4,7</span>
            <div><Stars value={4.7} size="w-5 h-5" /><p className="text-sm text-slate-500 mt-1">12 avis</p></div>
          </div>
        )}
      </div>
      {live && data.reviews?.length > 0 && (
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="google-reviews-list">
          {data.reviews.map((r, i) => <ReviewCard key={i} r={r} i={i} />)}
        </div>
      )}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <a href={viewUrl} target="_blank" rel="noopener noreferrer" data-testid="confiance-view-reviews-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A1864] text-white px-6 py-3 font-semibold hover:bg-[#102692] transition-colors">
          <ExternalLink className="w-4 h-4" /> Voir tous les avis sur Google
        </a>
        <a href={writeUrl} target="_blank" rel="noopener noreferrer" data-testid="confiance-leave-review-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0A1864] text-[#0A1864] px-6 py-3 font-semibold hover:bg-[#0A1864] hover:text-white transition-colors">
          <PenLine className="w-4 h-4" /> Laisser un avis
        </a>
      </div>
    </div>
  );
};
